import {
    Fragment,
    useEffect,
    useRef,
    useState
} from "react";
import {
    useNavigate,
    useParams
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { XMarkIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@primer/octicons-react";

import {
    classNames,
    prettySmartDateTime,
    setDocumentTitle
} from "../lib/utils";
import { IItem } from "../lib/types";
import { BackendObj } from "../lib/backend";
import { selectEnv, selectUser, startCelebration } from "../lib/scraper.slice";
import { USER_ROLES } from "../lib/consts";
import {
    IItemValidationSummary,
    ITableRowFilter,
} from "../lib/backend/extractions.types.generated";

import { LoadingSpinner } from "../components/LoadingSpinner";
import {
    ItemTableHandles,
    ItemTables
} from "../components/ItemTables";
import { Toggle } from "../components/Toggle";
import { Button } from "../components/Button";
import { ItemContent } from "../components/ItemContent";
import { TwoRowHeader } from "../components/Header";
import { ItemBreadcrumb } from "../components/ItemBreadcrumb";
import { ITableRowDisplay } from "../components/VirtualizedDataGrid";
import { IFieldRangeConfig } from "../components/ItemTables";

export function ItemConfirm() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { item_uuid } = useParams<{ item_uuid: string | undefined }>();

    const user = useSelector(selectUser);
    const env = useSelector(selectEnv);
    const is_admin = user.role === USER_ROLES.admin

    const [item, setItem] = useState<IItem | undefined>(undefined);
    const [field_range_configs, setFieldRangeConfigs] = useState<Record<string, IFieldRangeConfig>>({});
    const [field_range_configs_loading, setFieldRangeConfigsLoading] = useState(true);
    const [validation_summary, setValidationSummary] = useState<IItemValidationSummary | undefined>(undefined);
    const [invalid_uuid, setInvalidUuid] = useState(false);
    const [show_split, _setShowSplit] = useState(localStorage.getItem("show_confirm_split") ? localStorage.getItem("show_confirm_split") === "true" : true);
    const [left_width, setLeftWidth] = useState(localStorage.getItem("confirm_split_width") ? parseInt(localStorage.getItem("confirm_split_width") as string) : 50);
    const [is_dragging, setIsDragging] = useState(false);
    const [is_confirming, setIsConfirming] = useState(false);
    const [is_rejecting, setIsRejecting] = useState(false);

    useEffect(() => {
        if (item_uuid === undefined) { return; }
        BackendObj.extractions.getItem({ item_uuid })
            .then(({ item, validation_summary, message }) => {
                if (item) { setItem(item); }
                else if (message) { setInvalidUuid(true); }
                if (validation_summary) { setValidationSummary(validation_summary); }
            })
            .catch(() => setInvalidUuid(true));
    }, [item_uuid]);

    useEffect(() => {
        if (item === undefined) { return; }
        setFieldRangeConfigsLoading(true);
        const field_range_configs: Record<string, IFieldRangeConfig> = {};
        const loading_promises: Promise<void>[] = [];

        // collect all field ranges
        for (const context of item.template.contexts) {
            for (const field of context.fields) {
                // check if user defined ranges are present
                if (field.range !== undefined) {
                    const field_range = field.range;
                    switch (field_range.type) {
                        case "lookup_table_value_range": {
                            if (field_range.lookup_table_uuid === undefined || field_range.header_uuid === undefined || field_range.display_header_uuids === undefined) {
                                console.error(`Field range is missing required fields: ${field.uuid}`);
                                break;
                            }

                            const loading_promise = BackendObj.extractions.getLookupTable({ lookup_table_uuid: field_range.lookup_table_uuid }).then(({ lookup_table }) => {
                                if (lookup_table === undefined) {
                                    console.error(`Lookup table not found: ${field_range.lookup_table_uuid}`);
                                    return;
                                }

                                const displayed_headers = [
                                    ...lookup_table.headers
                                        .filter(h => field_range.header_uuid === h.uuid)
                                        .map(h => ({ ...h, pinned: "left" as const })),
                                    ...(field_range.display_header_uuids ?? [])
                                        .flatMap(uuid => {
                                            const header = lookup_table.headers.find(h => h.uuid === uuid);
                                            return header ? [header] : [];
                                        })
                                ];

                                const hidden_headers = lookup_table.headers.filter(h => !displayed_headers.some(dh => dh.uuid === h.uuid)).map(h => ({ ...h, is_hidden: true }));

                                const default_filters: ITableRowFilter[] = [{
                                    column_uuid: field_range.header_uuid,
                                    operator: "ne",
                                    values: [""],

                                }];

                                field_range_configs[field.uuid] = {
                                    type: "backend_fetch",
                                    headers: [...displayed_headers, ...hidden_headers],
                                    no_of_rows: lookup_table.no_of_rows,
                                    connected_fields: field_range.connected_fields,
                                    multi_line_input: field_range.multi_line_input,
                                    pivot_fields: field_range.pivot_fields,
                                    header_uuid: field_range.header_uuid,
                                    getValues: async (offset: number, limit: number, filters: ITableRowFilter[]): Promise<{ rows: ITableRowDisplay[] }> => {
                                        const { aggregated_rows } = await BackendObj.extractions.listLookupTableRowsAggregated({
                                            lookup_table_uuid: lookup_table.uuid,
                                            offset,
                                            limit,
                                            filters: [...filters, ...default_filters],
                                            aggregation_filters: {
                                                aggregation_columns: displayed_headers.map(header => ({
                                                    column_uuid: header.uuid,
                                                    function: "distinct",
                                                    alias: header.uuid
                                                }))
                                            }
                                        });

                                        return { rows: aggregated_rows };
                                    }
                                };
                            }).catch((error) => {
                                console.error(`Error loading lookup table for field ${field.uuid}:`, error);
                            });
                            loading_promises.push(loading_promise);
                            break;
                        }
                        default: {
                            console.error(`Unknown range type: ${field.range.type}`);
                            break;
                        }
                    }
                // apply internal ranges
                } else if (field.datatype === "enum" && field.extract?.enum_data?.type === "list") {
                    field_range_configs[field.uuid] = {
                        type: "values",
                        values: field.extract?.enum_data?.values?.map(v => v.value) ?? []
                    };
                }
            }
        }

        Promise.all(loading_promises).finally(() => {
            setFieldRangeConfigs(field_range_configs);
            setFieldRangeConfigsLoading(false);
        });
    }, [item])

    useEffect(() => {
        if (item === undefined) {
            setDocumentTitle("TODO - loading...", env);
        } else {
            setDocumentTitle(`TODO - ${item.name.length > 0 ? item.name : "[no subject]"}`, env);
        }
    }, [env, item]);

    const setShowSplit = (show: boolean) => {
        _setShowSplit(show);
        localStorage.setItem("show_confirm_split", show.toString());
    }

    /// CALLS TO CHILDREN

    const item_tables_ref = useRef<ItemTableHandles>(null);

    const handleConfirm = async () => {
        setIsConfirming(true);
        await item_tables_ref.current?.confirm();
        setIsConfirming(false);
    }

    const handleReject = async () => {
        setIsRejecting(true);
        await item_tables_ref.current?.reject();
        setIsRejecting(false);
    }

    /// COLUMN DRAGGING

    const startDragging = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault(); // Prevent text selection
        setIsDragging(true);
    };

    const stopDragging = () => {
        if (is_dragging) { setIsDragging(false); }
    };

    const drag = (e: MouseEvent) => {
        if (is_dragging) {
            // offset all coordinates for the size of the sidebar (256px or 80px)
            const client_x = e.clientX - 80;
            const window_width = window.innerWidth - 80;
            // compute the new width as a percentage of the window width
            const new_width = (client_x / window_width) * 100;
            setLeftWidth(new_width);
            localStorage.setItem("confirm_split_width", new_width.toString());
        }
    };

    useEffect(() => {
        window.addEventListener('mousemove', drag);
        window.addEventListener('mouseup', stopDragging);

        return () => {
            window.removeEventListener('mousemove', drag);
            window.removeEventListener('mouseup', stopDragging);
        };
    });

    const onItemUpdate = async () => {
        if (item === undefined) { return; }
        const { confirm_items } = await BackendObj.extractions.getOpenExtractConfirmations({});
        if (confirm_items.length > 0) {
            setItem(undefined);
            navigate(`/confirm/${confirm_items[0].uuid}`);
        } else {
            // confetti since no more tasks!
            dispatch(startCelebration());
            navigate(`/tasks`);
        }
    }

    const changeDecimalSeparator = async (decimal_separator: "," | ".") => {
        if (item === undefined) { return; }
        const { item: new_item } = await BackendObj.extractions.executeItemPostprocessing({ item_uuid: item.uuid, overrides: { decimal_separator } });
        setItem(new_item);
    }

    /// RENDER

    if (item_uuid === undefined || invalid_uuid) {
        return <div className={classNames("flex-row lg:fixed lg:right-0 lg:inset-y-0 overflow-y-auto m-10 lg:left-20")}>
            Item not found
        </div>;
    }

    if (item === undefined || field_range_configs_loading) {
        return <div className={classNames("flex-row lg:fixed lg:right-0 lg:inset-y-0 overflow-y-auto lg:left-20")}>
            <LoadingSpinner />
        </div>;
    }

    if (item.extract_confirmations_status !== "pending") {
        return <div className={classNames("flex lg:fixed lg:right-0 lg:inset-y-0 overflow-y-auto lg:left-20")}>
            <div className="p-10 w-full">
                <div className="max-w-5xl  flex flex-row items-center">
                    <div className="text-xl font-semibold leading-7 text-gray-900 sm:truncate sm:text-2xl sm:tracking-tight whitespace-nowrap truncate">
                        This item has already been processed
                    </div>
                    <div className="flex-grow" />
                    <Button text="Next Confirmation" highlight={true} onClick={onItemUpdate} />
                </div>
            </div>
        </div >;
    }

    return <div className={classNames("lg:fixed lg:right-0 lg:inset-y-0 overflow-y-auto lg:left-20")}>
        <div className="h-full w-full overflow-auto">
            <TwoRowHeader
                title={item.name.length > 0 ? item.name : "[no subject]"}
                subtitle={`${item.template.name} [${prettySmartDateTime(item.created_at)}]`}
                buttons={<Fragment>
                    <Button
                        icon={XMarkIcon}
                        text="Reject"
                        highlight={validation_summary?.final_status === "reject"}
                        disabled={is_confirming || is_rejecting}
                        onClick={handleReject}
                        loading={is_rejecting} />
                    <Button
                        icon={CheckIcon}
                        text="Confirm"
                        highlight={validation_summary?.final_status !== "reject"}
                        disabled={is_confirming || is_rejecting}
                        onClick={handleConfirm}
                        loading={is_confirming} />
                </Fragment>}
                subbuttons={<div className="flex flex-row items-center gap-2">
                    <div className="text-xs text-gray-400 cursor-pointer" onClick={() => setShowSplit(!show_split)}>Show Input</div>
                    <Toggle enabled={show_split} setEnabled={setShowSplit} />
                </div>}
                full_width={true}
                onBack={() => navigate("/tasks")}
            />
            <div className="flex flex-row h-[calc(100%-5rem)]">
                {/* Left Column */}
                <div style={show_split ? { width: `${left_width}%` } : {}} className={classNames("h-full overflow-auto", show_split ? "" : "w-full")}>
                    {is_admin && <div className="flex items-center gap-2 text-gray-400 pl-5 pt-2">
                        <ItemBreadcrumb
                            template_uuid={item.template_uuid}
                            template_name={item.template.name}
                            endpoint_uuid={item.endpoint?.uuid}
                            endpoint_name={item.endpoint?.name}
                        />
                    </div>}
                    <div className={classNames("px-5", is_admin ? "py-2" : "py-4")}>
                        <ItemTables
                            ref={item_tables_ref}
                            item={item}
                            field_range_configs={field_range_configs}
                            validation_summary={validation_summary}
                            onItemUpdate={onItemUpdate}
                            changeDecimalSeparator={changeDecimalSeparator}
                        />
                    </div>
                </div>
                {/* Separator */}
                {show_split && <div onMouseDown={startDragging} className="cursor-col-resize bg-gray-300 hover:bg-sea_blue-700 w-1 h-full" />}
                {/* Right Column */}
                {show_split && <div style={{ width: `${100 - left_width}%` }} className="h-full overflow-auto" >
                    <div className="p-5">
                        <ItemContent org_uuid={item.template.org_uuid} item={item} attachments={item.attachments} contexts={item.template.contexts} />
                    </div>
                </div>}
            </div>
        </div>
    </div >;
}